import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { RoleProvider, useRole } from "@/store/role";
import { Toaster } from "@/components/ui/sonner";
import AppShell from "@/layout/AppShell";
import Login from "@/pages/Login";
import { PermissionRoute } from "@/components/PermissionRoute";
import { StudentRoute } from "@/components/StudentRoute";
import { HR_ROUTES } from "@/modules/hr/routes";
import { HOSTELS_ROUTES } from "@/modules/hostels/routes";
import { firstAllowedPath, isStudentUser, STUDENT_DEFAULT_HOME } from "@/config/navigation";
import { hasValidAccessToken, loginRedirectPath } from "@/lib/auth";
import SessionWatch from "@/components/SessionWatch";

const HostelCheckInPublic = lazy(() => import("@/pages/HostelCheckInPublic"));
const HostelTokenDisplay = lazy(() => import("@/pages/HostelTokenDisplay"));

const PUBLIC_PATHS = ["/login", "/hostel-check-in", "/hostel-check-in-display"];

function isPublicPath(pathname) {
  return PUBLIC_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

function HomeRedirect() {
  const { currentUser, permissions } = useRole();
  const to = firstAllowedPath(currentUser, permissions);
  if (to) return <Navigate to={to} replace />;
  if (isStudentUser(currentUser)) {
    return <Navigate to={STUDENT_DEFAULT_HOME} replace />;
  }
  return <Navigate to="/hr/dashboard" replace />;
}

function Boot({ children }) {
  const { ready } = useRole();
  const location = useLocation();
  const publicRoute = isPublicPath(location.pathname);
  const authed = hasValidAccessToken();

  if (publicRoute) return children;

  if (!authed) {
    return <Navigate to={loginRedirectPath()} replace />;
  }

  if (!ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-mahindra-lavender">
        <div className="text-sm font-medium text-mahindra-steel" data-testid="app-loading">
          Loading MU One…
        </div>
      </div>
    );
  }
  return children;
}

function AppShellRoutes() {
  return (
    <AppShell>
      <Suspense
        fallback={
          <div className="py-12 text-sm text-mahindra-steel" data-testid="route-loading">
            Loading…
          </div>
        }
      >
        <Routes>
          <Route path="/" element={<HomeRedirect />} />
          {HR_ROUTES.map(({ path, permission, Component }) => (
            <Route
              key={path}
              path={path}
              element={
                <PermissionRoute permissions={permission}>
                  <Component />
                </PermissionRoute>
              }
            />
          ))}
          {HOSTELS_ROUTES.map(({ path, permission, studentOnly, Component }) => (
            <Route
              key={path}
              path={path}
              element={
                studentOnly ? (
                  <StudentRoute>
                    <Component />
                  </StudentRoute>
                ) : (
                  <PermissionRoute permissions={permission}>
                    <Component />
                  </PermissionRoute>
                )
              }
            />
          ))}
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </Suspense>
    </AppShell>
  );
}

function RootRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        path="/hostel-check-in"
        element={
          <Suspense
            fallback={
              <div className="min-h-dvh flex items-center justify-center text-sm text-muted-foreground">
                Loading check-in…
              </div>
            }
          >
            <HostelCheckInPublic />
          </Suspense>
        }
      />
      <Route
        path="/hostel-check-in-display"
        element={
          <Suspense
            fallback={
              <div className="min-h-dvh flex items-center justify-center text-sm text-muted-foreground">
                Loading display…
              </div>
            }
          >
            <HostelTokenDisplay />
          </Suspense>
        }
      />
      <Route path="/*" element={<AppShellRoutes />} />
    </Routes>
  );
}

export default function App() {
  return (
    <RoleProvider>
      <BrowserRouter>
        <SessionWatch />
        <Boot>
          <RootRoutes />
        </Boot>
        <Toaster position="top-right" richColors closeButton />
      </BrowserRouter>
    </RoleProvider>
  );
}
